// Deterministic models of CollabBoard's suggestions route, for Lesson 3.5 ("A Suggestion Is a
// Proposal, Not a Write"). They follow the token through two designs, swap the model client for
// a fake, grow the model_calls log and turn proposals into tasks only when a person decides.
// They never send a request, store a row or bill anything.

import { outputCost } from './api-models';
import { inputCost, lessonNotes } from './model-models';

/** Fixed data shared by the four graphics (module-3/alignment.md, Lesson 3.5). */
export const PROMPT_VERSION = 'suggester-v1';
export const PROJECT_ID = 1;
export const PROJECT_NAME = 'Apollo';
export const FIRST_NEW_TASK_ID = 17;
export const SUGGESTION_TITLES: readonly [string, string, string] = [
  'Book the demo room',
  'Send the agenda to Ravi',
  'Check the projector',
];
export const FIXED_REPLY = `{"tasks": [${SUGGESTION_TITLES.map((title) => `{"title": "${title}"}`).join(', ')}]}`;
export const MODEL_ERROR_MESSAGE =
  'Model error 503: The model provider is unavailable. Try again shortly.';
export const UNAVAILABLE_BODY = '{"detail": "The model is unavailable. Try again shortly."}';
export const FORBIDDEN_BODY = '{"detail": "Project was not found"}';
/** Tokens one real call spends on note 1 with the suggester prompt. */
export const PROMPT_TOKENS = 164;
export const COMPLETION_TOKENS = 14;
export const REAL_LATENCY_S = 1.8;
export const noteOne = lessonNotes[0];

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}

// --- feature-flow: where the token lives ----------------------------------------------------

export type Design = 'page-calls-model' | 'page-calls-api';
export const designLabels: Record<Design, string> = {
  'page-calls-model': 'The page calls the model',
  'page-calls-api': 'The page calls our API',
};
export const toDesign = (value: string): Design =>
  value === 'page-calls-model' ? 'page-calls-model' : 'page-calls-api';
export type Actor = 'the browser' | 'our API' | 'the gateway' | 'the model';
export interface Hop {
  step: number;
  actor: Actor;
  text: string;
  /** Who holds the token while this hop happens. */
  tokenHolder: string;
  /** What the browser's developer tools can show once this hop has happened. */
  devTools: string;
}
const TOKEN_IN_EVERY_REQUEST = 'the token, in every request';
const NOTE_ONLY = 'the note, never the token';
const NOTE_AND_SUGGESTIONS = 'the note and suggestions, never the token';
const pageCallsModel: readonly Hop[] = [
  {
    step: 1,
    actor: 'the browser',
    text: 'The browser builds the request to the gateway with the token in its Authorization header.',
    tokenHolder: "the browser, in the page's JavaScript",
    devTools: TOKEN_IN_EVERY_REQUEST,
  },
  {
    step: 2,
    actor: 'the gateway',
    text: 'The gateway checks the token and forwards the request to the model.',
    tokenHolder: 'the gateway, as the browser sent it',
    devTools: TOKEN_IN_EVERY_REQUEST,
  },
  {
    step: 3,
    actor: 'the model',
    text: 'The model replies with the suggestions.',
    tokenHolder: 'the browser, ready for the next request',
    devTools: TOKEN_IN_EVERY_REQUEST,
  },
  {
    step: 4,
    actor: 'the browser',
    text: 'The browser shows the suggestions.',
    tokenHolder: 'the browser, still in the page',
    devTools: TOKEN_IN_EVERY_REQUEST,
  },
];
const pageCallsApi: readonly Hop[] = [
  {
    step: 1,
    actor: 'the browser',
    text: `The browser sends POST /projects/${PROJECT_ID}/suggestions with the note. No token.`,
    tokenHolder: 'our API, in its environment',
    devTools: NOTE_ONLY,
  },
  {
    step: 2,
    actor: 'our API',
    text: 'Our API reads the token from its environment and calls the gateway.',
    tokenHolder: 'our API, from its environment',
    devTools: NOTE_ONLY,
  },
  {
    step: 3,
    actor: 'the gateway',
    text: 'The gateway checks the token.',
    tokenHolder: 'the gateway, as our API sent it',
    devTools: NOTE_ONLY,
  },
  {
    step: 4,
    actor: 'the model',
    text: 'The model replies to our API.',
    tokenHolder: 'our API',
    devTools: NOTE_ONLY,
  },
  {
    step: 5,
    actor: 'our API',
    text: 'Our API stores a model_calls row and answers the browser with the suggestions.',
    tokenHolder: 'our API',
    devTools: NOTE_AND_SUGGESTIONS,
  },
  {
    step: 6,
    actor: 'the browser',
    text: 'The browser shows the suggestions.',
    tokenHolder: 'our API, never the browser',
    devTools: NOTE_AND_SUGGESTIONS,
  },
];
export const hopsFor = (design: Design): readonly Hop[] =>
  design === 'page-calls-model' ? pageCallsModel : pageCallsApi;
export interface FlowState {
  design: Design;
  /** How many hops have happened; 0 before the first press. */
  hop: number;
}
export const initialFlow = (): FlowState => ({ design: 'page-calls-model', hop: 0 });
export const currentHop = (state: FlowState): Hop | undefined =>
  hopsFor(state.design)[state.hop - 1];
export const flowDone = (state: FlowState) => state.hop >= hopsFor(state.design).length;
/** The hops so far, the current one marked with an arrow. */
export function hopLines(state: FlowState): string[] {
  return hopsFor(state.design)
    .slice(0, state.hop)
    .map((hop) => `${hop.step === state.hop ? '→' : ' '} ${hop.step} ${hop.actor}: ${hop.text}`);
}
export interface FlowFacts {
  currentHop: string;
  tokenHolder: string;
  devTools: string;
}
export function flowFacts(state: FlowState): FlowFacts {
  const total = hopsFor(state.design).length;
  const hop = currentHop(state);
  if (!hop)
    return {
      currentHop: `none yet (0 of ${total})`,
      tokenHolder:
        state.design === 'page-calls-model'
          ? "the browser, in the page's JavaScript"
          : 'our API, on the server',
      devTools:
        state.design === 'page-calls-model' ? "the token, in the page's JavaScript" : 'nothing yet',
    };
  return {
    currentHop: `${hop.step} of ${total}: ${hop.actor}`,
    tokenHolder: hop.tokenHolder,
    devTools: hop.devTools,
  };
}
export type FlowAction = { type: 'design'; value: Design } | { type: 'next' } | { type: 'reset' };
export function flowTransition(state: FlowState, action: FlowAction): FlowState {
  switch (action.type) {
    case 'reset':
      return initialFlow();
    case 'design':
      return { design: action.value, hop: 0 };
    case 'next':
      return flowDone(state) ? state : { ...state, hop: state.hop + 1 };
  }
}
export function flowSummary(state: FlowState): Summary {
  if (state.hop === 0)
    return {
      tone: 'neutral',
      message: 'Nothing sent yet. Press Next hop to follow the token.',
    };
  return state.design === 'page-calls-model'
    ? {
        tone: 'warning',
        message: 'Anyone who opens the developer tools copies the token and spends on our account.',
      }
    : {
        tone: 'success',
        message: 'The token never leaves the server. The browser never sees it.',
      };
}

// --- feature-dependency: a test must not call the model ------------------------------------

export type ClientKind = 'real' | 'fixed' | 'raising';
export const clientLabels: Record<ClientKind, string> = {
  real: 'The real client',
  fixed: 'A fake with a fixed reply',
  raising: 'A fake that raises ModelError',
};
export const toClientKind = (value: string): ClientKind =>
  value === 'real' || value === 'fixed' || value === 'raising' ? value : 'real';
export const DEPENDS_LINE = 'client: ModelClient = Depends(get_model_client)';
export function wiringLines(client: ClientKind): string[] {
  switch (client) {
    case 'real':
      return [DEPENDS_LINE, '# no override'];
    case 'fixed':
      return [
        DEPENDS_LINE,
        `app.dependency_overrides[get_model_client] = lambda: FakeClient(reply='${FIXED_REPLY}')`,
      ];
    case 'raising':
      return [
        DEPENDS_LINE,
        `app.dependency_overrides[get_model_client] = lambda: FakeClient(raises=ModelError("${MODEL_ERROR_MESSAGE}"))`,
      ];
  }
}
export interface ClientReply {
  status: number;
  body: string;
  seconds: number;
  /** Requests that reach the model for one send. */
  requests: number;
  promptTokens: number;
  completionTokens: number;
}
export function replyFor(client: ClientKind): ClientReply {
  switch (client) {
    case 'real':
      return {
        status: 200,
        body: FIXED_REPLY,
        seconds: REAL_LATENCY_S,
        requests: 1,
        promptTokens: PROMPT_TOKENS,
        completionTokens: COMPLETION_TOKENS,
      };
    case 'fixed':
      return {
        status: 200,
        body: FIXED_REPLY,
        seconds: 0,
        requests: 0,
        promptTokens: 0,
        completionTokens: 0,
      };
    case 'raising':
      return {
        status: 503,
        body: UNAVAILABLE_BODY,
        seconds: 0,
        requests: 0,
        promptTokens: 0,
        completionTokens: 0,
      };
  }
}
export const responseLines = (reply: ClientReply) => [
  `${reply.status} ${reply.body}`,
  `after ${reply.seconds.toFixed(1)} s`,
];
export interface DependencyState {
  client: ClientKind;
  /** The client the last send used, or null when nothing has been sent since the wiring changed. */
  sent: ClientKind | null;
  /** Totals since Reset, across every client tried. */
  modelRequests: number;
  promptTokens: number;
  completionTokens: number;
}
export const initialDependency = (): DependencyState => ({
  client: 'real',
  sent: null,
  modelRequests: 0,
  promptTokens: 0,
  completionTokens: 0,
});
export type DependencyAction =
  { type: 'client'; value: ClientKind } | { type: 'send' } | { type: 'reset' };
export function dependencyTransition(
  state: DependencyState,
  action: DependencyAction,
): DependencyState {
  switch (action.type) {
    case 'reset':
      return initialDependency();
    case 'client':
      return { ...state, client: action.value, sent: null };
    case 'send': {
      const reply = replyFor(state.client);
      return {
        ...state,
        sent: state.client,
        modelRequests: state.modelRequests + reply.requests,
        promptTokens: state.promptTokens + reply.promptTokens,
        completionTokens: state.completionTokens + reply.completionTokens,
      };
    }
  }
}
export const tokensSpent = (state: DependencyState) =>
  state.promptTokens + state.completionTokens === 0
    ? '0'
    : `${state.promptTokens} prompt + ${state.completionTokens} completion`;
export const timeTaken = (state: DependencyState) =>
  state.sent === null ? '(nothing sent)' : `${replyFor(state.sent).seconds.toFixed(1)} s`;
export function dependencySummary(state: DependencyState): Summary {
  switch (state.sent) {
    case null:
      return {
        tone: 'neutral',
        message: 'Nothing sent yet. The wiring shows which client the route gets.',
      };
    case 'real':
      return {
        tone: 'warning',
        message: 'Every test run would send a real request: slow, billed and different each time.',
      };
    case 'fixed':
      return {
        tone: 'success',
        message: 'The route ran against a fixed reply: fast, free and the same every time.',
      };
    case 'raising':
      return {
        tone: 'success',
        message:
          'The fake raised ModelError and the route answered 503: the failure path is tested without a failure.',
      };
  }
}

// --- feature-log: every call is written down ----------------------------------------------

export type Outcome = 'ok' | 'cut' | 'invalid-json' | 'model-error';
export interface OutcomeSpec {
  label: string;
  /** The value the outcome column stores. */
  stored: string;
  promptTokens: number;
  completionTokens: number;
  latencyMs: number;
}
export const outcomeSpecs: Record<Outcome, OutcomeSpec> = {
  ok: { label: 'ok', stored: 'ok', promptTokens: 164, completionTokens: 14, latencyMs: 1800 },
  cut: { label: 'cut', stored: 'cut', promptTokens: 164, completionTokens: 60, latencyMs: 2400 },
  'invalid-json': {
    label: 'invalid JSON',
    stored: 'invalid_json',
    promptTokens: 164,
    completionTokens: 31,
    latencyMs: 1900,
  },
  'model-error': {
    label: 'model error',
    stored: 'model_error',
    promptTokens: 0,
    completionTokens: 0,
    latencyMs: 300,
  },
};
export const OUTCOMES: readonly Outcome[] = ['ok', 'cut', 'invalid-json', 'model-error'];
export const toOutcome = (value: string): Outcome =>
  OUTCOMES.find((outcome) => outcome === value) ?? 'ok';
export interface ModelCallRow {
  id: number;
  promptVersion: string;
  noteId: 1 | 2 | 3;
  promptTokens: number;
  completionTokens: number;
  latencyMs: number;
  outcome: Outcome;
}
export const callCost = (row: Pick<ModelCallRow, 'promptTokens' | 'completionTokens'>) =>
  Number(inputCost(row.promptTokens)) + Number(outputCost(row.completionTokens));
export const totalCost = (rows: readonly ModelCallRow[]) =>
  rows.reduce((sum, row) => sum + callCost(row), 0).toFixed(6);
export const failureCount = (rows: readonly ModelCallRow[]) =>
  rows.filter((row) => row.outcome !== 'ok').length;
export interface LogState {
  outcome: Outcome;
  rows: ModelCallRow[];
}
export const initialLog = (): LogState => ({ outcome: 'ok', rows: [] });
const NOTE_CYCLE: readonly [1, 2, 3] = [1, 2, 3];
export function rowFor(outcome: Outcome, id: number): ModelCallRow {
  const spec = outcomeSpecs[outcome];
  return {
    id,
    promptVersion: PROMPT_VERSION,
    noteId: NOTE_CYCLE[(id - 1) % NOTE_CYCLE.length] ?? 1,
    promptTokens: spec.promptTokens,
    completionTokens: spec.completionTokens,
    latencyMs: spec.latencyMs,
    outcome,
  };
}
export type LogAction = { type: 'outcome'; value: Outcome } | { type: 'ask' } | { type: 'reset' };
export function logTransition(state: LogState, action: LogAction): LogState {
  switch (action.type) {
    case 'reset':
      return initialLog();
    case 'outcome':
      return { ...state, outcome: action.value };
    case 'ask':
      return { ...state, rows: [...state.rows, rowFor(state.outcome, state.rows.length + 1)] };
  }
}
export function logSummary(state: LogState): Summary {
  const last = state.rows.at(-1);
  if (!last)
    return {
      tone: 'neutral',
      message: 'Nothing asked yet. Every call will leave one row.',
    };
  if (last.outcome !== 'ok')
    return {
      tone: 'warning',
      message: 'The failure left a row too. A log that skips failures hides the calls you need.',
    };
  const count = state.rows.length;
  return {
    tone: 'success',
    message: `${count} call${count === 1 ? '' : 's'}, ${totalCost(state.rows)} in total: the cost is now one query away.`,
  };
}

// --- feature-decision: the page shows proposals, Maya decides -----------------------------

export type ActingUser = 'maya' | 'sam';
export const actingUserLabels: Record<ActingUser, string> = {
  maya: `Maya, manager of ${PROJECT_NAME}`,
  sam: 'Sam, not a member',
};
export const toActingUser = (value: string): ActingUser => (value === 'sam' ? 'sam' : 'maya');
export type SuggestionStatus = 'pending' | 'accepted' | 'dismissed';
export interface SuggestionRow {
  id: number;
  title: string;
  status: SuggestionStatus;
}
export interface TaskRow {
  id: number;
  title: string;
  projectId: number;
  /** The assignee's user id, or null when nobody has been assigned. */
  assigneeId: number | null;
}
/** Apollo's existing tasks from the shared dataset: 9 belongs to Ravi (2), 10 to nobody. */
export const existingTasks: readonly TaskRow[] = [
  { id: 9, title: 'Write the release note', projectId: PROJECT_ID, assigneeId: 2 },
  { id: 10, title: 'Check the dates', projectId: PROJECT_ID, assigneeId: null },
];
export type Decision = 'accepted' | 'refused' | 'dismissed';
export interface DecisionState {
  actor: ActingUser;
  suggestions: SuggestionRow[];
  tasks: TaskRow[];
  lastResponse: string | null;
  last: { decision: Decision; taskId?: number } | null;
}
export const initialDecision = (): DecisionState => ({
  actor: 'maya',
  suggestions: SUGGESTION_TITLES.map((title, index) => ({
    id: index + 1,
    title,
    status: 'pending',
  })),
  tasks: [...existingTasks],
  lastResponse: null,
  last: null,
});
export const pendingCount = (state: DecisionState) =>
  state.suggestions.filter((row) => row.status === 'pending').length;
export const acceptedCount = (state: DecisionState) =>
  state.suggestions.filter((row) => row.status === 'accepted').length;
export const tasksCreated = (state: DecisionState) => state.tasks.length - existingTasks.length;
export const nextTaskId = (state: DecisionState) => FIRST_NEW_TASK_ID + tasksCreated(state);
export type DecisionAction =
  | { type: 'actor'; value: ActingUser }
  | { type: 'accept'; id: number }
  | { type: 'dismiss'; id: number }
  | { type: 'reset' };
const taskBody = (task: TaskRow) =>
  `{"id": ${task.id}, "title": "${task.title}", "project_id": ${task.projectId}, "assignee_id": ${task.assigneeId ?? 'null'}}`;
export function decisionTransition(state: DecisionState, action: DecisionAction): DecisionState {
  switch (action.type) {
    case 'reset':
      return initialDecision();
    case 'actor':
      return { ...state, actor: action.value };
    case 'accept':
    case 'dismiss': {
      const suggestion = state.suggestions.find((row) => row.id === action.id);
      if (suggestion?.status !== 'pending') return state;
      if (state.actor === 'sam')
        return { ...state, lastResponse: `404 ${FORBIDDEN_BODY}`, last: { decision: 'refused' } };
      if (action.type === 'dismiss')
        return {
          ...state,
          suggestions: state.suggestions.map((row) =>
            row.id === action.id ? { ...row, status: 'dismissed' } : row,
          ),
          lastResponse: `200 {"id": ${action.id}, "status": "dismissed"}`,
          last: { decision: 'dismissed' },
        };
      const task: TaskRow = {
        id: nextTaskId(state),
        title: suggestion.title,
        projectId: PROJECT_ID,
        assigneeId: null,
      };
      return {
        ...state,
        suggestions: state.suggestions.map((row) =>
          row.id === action.id ? { ...row, status: 'accepted' } : row,
        ),
        tasks: [...state.tasks, task],
        lastResponse: `201 ${taskBody(task)}`,
        last: { decision: 'accepted', taskId: task.id },
      };
    }
  }
}
export function decisionSummary(state: DecisionState): Summary {
  switch (state.last?.decision) {
    case undefined:
      return {
        tone: 'neutral',
        message: 'Three proposals, zero tasks. Nothing changes until a person presses a button.',
      };
    case 'accepted':
      return {
        tone: 'success',
        message: `Accepted: the existing create-task service inserted task ${state.last.taskId ?? nextTaskId(state) - 1} with Maya's permissions.`,
      };
    case 'refused':
      return {
        tone: 'warning',
        message:
          "Refused with 404: Sam is not a member, so the model's proposal gave him no new rights.",
      };
    case 'dismissed':
      return {
        tone: 'neutral',
        message: 'Dismissed: the decision is stored and no task exists.',
      };
  }
}
